import { useEffect, useId, useRef, useState } from 'react';
import { CaretDownIcon, ExportIcon } from './icons';
import type { ExportFormat } from '../types';

/**
 * Export dropdown (Figma "Export Button": label + blue caret segment). CSV and Excel are generated by the server for the active filters; PDF is shown
 * disabled with a "coming soon" label (BACKEND GAP: no PDF library yet), never faked.
 */
export function ExportMenu({ onExport, busy, disabled }: { onExport: (format: ExportFormat) => void; busy: boolean; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const uid = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function close(refocus: boolean) {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }

  function pick(format: ExportFormat) {
    close(true);
    onExport(format);
  }

  return (
    <div className="cp-export" ref={wrap} onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); close(true); } }}>
      <button
        ref={trigger}
        type="button"
        className="cp-btn cp-btn--export"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`${uid}-menu`}
        disabled={disabled || busy}
        onClick={() => setOpen((v) => !v)}
      >
        <ExportIcon />
        <span>{busy ? 'Exporting…' : 'Export'}</span>
        <span className="cp-btn__caret" aria-hidden="true"><CaretDownIcon /></span>
      </button>
      {open && (
        <ul className="cp-menu" id={`${uid}-menu`} role="menu" aria-label="Export format">
          <li role="none">
            <button type="button" role="menuitem" className="cp-menu__item" aria-disabled="true" title="PDF export is coming soon" onClick={(e) => e.preventDefault()}>
              PDF <span className="cp-menu__soon">Coming soon</span>
            </button>
          </li>
          <li role="none"><button type="button" role="menuitem" className="cp-menu__item" onClick={() => pick('xlsx')}>Excel (.xlsx)</button></li>
          <li role="none"><button type="button" role="menuitem" className="cp-menu__item" onClick={() => pick('csv')}>CSV (.csv)</button></li>
        </ul>
      )}
    </div>
  );
}
